/**
 * Αρχείο Video as an LTO MAM. The header gives the archive's figures (tapes, items, items with a preview copy, what
 * is on tape, restores under way) and whether the archive agent at the station is online. Three views: the search
 * (the HPress fields, a text box over titles, summaries and tape codes, results with their picture, tape and state,
 * the robot fetching the tape while the archive is searched; the chosen item in the panel), the tapes, and the
 * restores. The item's card (create, edit, history) is the HPress one (`videos.tsx`), with one row more: the video
 * its preview is made from (`archive-ingest.tsx`); the previews being made are followed in a tray in the corner.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import '../archive.css';
import { formatBytes } from '@airpost/shared';
import { ApiError, del, get } from '../api.js';
import { t } from '../i18n.js';
import { DateInput } from '../date-input.js';
import { navigate } from '../router.js';
import { useSession } from '../session.js';
import { Cartridge, TapeRobot } from '../tape-robot.js';
import { fmtDateInput, hhmmss } from '../util.js';
import { onPreviewDone, startPreview } from '../archive-preview.js';
import { PreviewPicker, PreviewTray, pickReady, readPicked, type PickedVideo, type ServerIngest } from './archive-ingest.js';
import { ItemPanel } from './archive-item.js';
import { RestoresView } from './archive-restores.js';
import { TapesView } from './archive-tapes.js';
import { VideoDialog, type ListResult, type Options, type VideoDto } from './videos.js';

interface Summary {
  tapes: number;
  items: number;
  withProxy: number;
  withFile: number;
  onTapeBytes: number;
  restoresOpen: number;
  agent: { online: boolean; seenAt: string; backend: string; version: string; targets: Array<{ key: string; label: string }>; loaded: string | null } | null;
  ingest: ServerIngest;
}
type View = 'search' | 'tapes' | 'restores';

/** The robot's one trip from the slot to the drive: a search shows at least that much of it. */
const ROBOT_TRIP_MS = 1450;
const calm = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
/** What the robot goes for: the tape asked for, a tape code typed in the box, or any tape. */
const BARCODE_LIKE = /^[A-Za-z]{1,4}\d{2,6}$/;
const nf = new Intl.NumberFormat('el-GR');

export function VideoArchivePage({ openNew = false }: { openNew?: boolean }) {
  const { can } = useSession();
  const [view, setView] = useState<View>('search');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [options, setOptions] = useState<Options>({ types: [], categories: [] });
  const [dialog, setDialog] = useState<{ mode: 'new' } | { mode: 'edit'; video: VideoDto } | null>(openNew ? { mode: 'new' } : null);
  const [openTapeId, setOpenTapeId] = useState<string | null>(null);
  /** The video picked in the card for the item's preview. */
  const [picked, setPicked] = useState<PickedVideo | null>(null);
  /** Moves on when an item's preview has been kept: the panel showing it is drawn again. */
  const [previewTick, setPreviewTick] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  // the search
  const [q, setQ] = useState('');
  const [videoType, setVideoType] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [tapeNo, setTapeNo] = useState('');
  const [itemNo, setItemNo] = useState('');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [onlyProxy, setOnlyProxy] = useState(false);
  const [tapeId, setTapeId] = useState<{ id: string; barcode: string } | null>(null);
  const [limit, setLimit] = useState(100);
  const [withSummary, setWithSummary] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [data, setData] = useState<ListResult | null>(null);
  const [searching, setSearching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<VideoDto | null>(null);
  const seq = useRef(0);
  const list = useRef<HTMLDivElement | null>(null);

  const loadSummary = useCallback(() => {
    get<Summary>('/api/archive/summary')
      .then(setSummary)
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    loadSummary();
    get<Options>('/api/videos/options')
      .then(setOptions)
      .catch(() => undefined);
    const h = window.setInterval(loadSummary, 30_000);
    return () => window.clearInterval(h);
  }, [loadSummary]);

  const params = useCallback(() => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (videoType) p.set('videoType', videoType);
    if (eventDate) p.set('eventDate', eventDate);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (tapeNo.trim()) p.set('tapeNo', tapeNo.trim());
    if (itemNo.trim()) p.set('itemNo', itemNo.trim());
    if (categoryIds.length) p.set('categoryIds', categoryIds.join(','));
    if (q.trim()) p.set('q', q.trim());
    if (onlyProxy) p.set('hasProxy', '1');
    if (tapeId) p.set('tapeId', tapeId.id);
    return p;
  }, [limit, videoType, eventDate, from, to, tapeNo, itemNo, categoryIds, q, onlyProxy, tapeId]);

  const search = useCallback(
    async (keep?: string) => {
      const mine = ++seq.current;
      const target = tapeId?.barcode ?? (tapeNo.trim() || (BARCODE_LIKE.test(q.trim()) ? q.trim() : 'LTO'));
      setSearching(target.toUpperCase());
      setError(null);
      const started = performance.now();
      try {
        const d = await get<ListResult>(`/api/videos?${params()}`);
        const wait = ROBOT_TRIP_MS - (performance.now() - started);
        if (wait > 0 && !calm()) await new Promise((r) => window.setTimeout(r, wait));
        if (mine !== seq.current) return;
        setData(d);
        setSelected((cur) => d.videos.find((v) => v.id === (keep ?? cur?.id)) ?? d.videos[0] ?? null);
      } catch (e) {
        if (mine === seq.current) setError(e instanceof ApiError ? e.message : String(e));
      } finally {
        if (mine === seq.current) setSearching(null);
      }
    },
    [params, q, tapeNo, tapeId],
  );
  // the first search, and again when a tape is chosen from the tapes
  useEffect(() => {
    const h = window.setTimeout(() => void search(), 0);
    return () => window.clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tapeId]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void search();
  };
  const clear = () => {
    setQ('');
    setVideoType('');
    setEventDate('');
    setFrom('');
    setTo('');
    setTapeNo('');
    setItemNo('');
    setCategoryIds([]);
    setOnlyProxy(false);
    setTapeId(null);
  };
  const toggleCategory = (id: string) => setCategoryIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  /** An item chosen from a tape or a restore: the search shows it (with its tape's items when it came from a tape). */
  const openItem = useCallback(async (videoId: string, fromTape?: string) => {
    setView('search');
    try {
      const r = await get<{ video: VideoDto }>(`/api/videos/${videoId}`);
      if (fromTape && r.video.tapeNo) {
        setTapeId({ id: fromTape, barcode: r.video.tapeNo });
        setSelected(r.video);
      } else setSelected(r.video);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, []);
  const openTape = (id: string) => {
    setOpenTapeId(id);
    setView('tapes');
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!data?.videos.length || (e.target as HTMLElement).closest('input, textarea, select')) return;
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const i = data.videos.findIndex((v) => v.id === selected?.id);
    const next = data.videos[Math.min(data.videos.length - 1, Math.max(0, i + (e.key === 'ArrowDown' ? 1 : -1)))];
    if (next) {
      setSelected(next);
      list.current?.querySelector(`[data-id="${next.id}"]`)?.scrollIntoView({ block: 'nearest' });
    }
  };

  const closeDialog = () => {
    setDialog(null);
    setPicked(null);
    if (openNew) navigate('/video');
  };
  const onSaved = (video: VideoDto) => {
    setNotice(t('videoSaved'));
    window.setTimeout(() => setNotice(null), 2500);
    // the video picked in the card: its preview is made now (here or on the server), followed in the tray; one still
    // being read is made once it has been
    const ingest = summary?.ingest ?? null;
    if (pickReady(picked, ingest)) startPreview(video.id, video.title, picked.file, picked.look, !!ingest?.server);
    else if (picked && !picked.look) readPicked(picked.file, (p) => pickReady(p, ingest) && startPreview(video.id, video.title, p.file, p.look, !!ingest?.server));
    closeDialog();
    // the item saved is the one shown, found by the search or not
    void search(video.id).then(() => setSelected((cur) => (cur?.id === video.id ? cur : video)));
    loadSummary();
  };
  // a preview kept: the results (their pictures), the figures and the item's panel are read again
  useEffect(
    () =>
      onPreviewDone((videoId) => {
        loadSummary();
        const proxyAt = Math.round(Date.now() / 1000);
        setData((d) => (d ? { ...d, videos: d.videos.map((v) => (v.id === videoId ? { ...v, hasProxy: true, proxyAt } : v)) } : d));
        setSelected((cur) => (cur?.id === videoId ? { ...cur, hasProxy: true, proxyAt } : cur));
        setPreviewTick((n) => n + 1);
        // the server may have given the item its length (TC τέλους) from the file
        get<{ video: VideoDto }>(`/api/videos/${videoId}`)
          .then(({ video }) => {
            const fresh = { ...video, proxyAt };
            setData((d) => (d ? { ...d, videos: d.videos.map((v) => (v.id === videoId ? fresh : v)) } : d));
            setSelected((cur) => (cur?.id === videoId ? fresh : cur));
          })
          .catch(() => undefined);
      }),
    [loadSummary],
  );
  const remove = async (v: VideoDto) => {
    if (!window.confirm(t('confirmDeleteVideo'))) return;
    try {
      await del(`/api/videos/${v.id}`);
      void search();
      loadSummary();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const agent = summary?.agent;
  return (
    <>
    <div className="lto" onKeyDown={onKey}>
      <header className="lto-head">
        <div className="lto-brand">
          <Cartridge barcode="ARCHIVE" generation="LTO-8" size="sm" />
          <div>
            <h1>{t('tabVideo')}</h1>
            <span>{t('ltoTagline')}</span>
          </div>
        </div>
        <div className="lto-stats">
          <Stat label={t('ltoStatTapes')} value={summary ? nf.format(summary.tapes) : '…'} />
          <Stat label={t('ltoStatItems')} value={summary ? nf.format(summary.items) : '…'} />
          <Stat label={t('ltoStatPreview')} value={summary ? nf.format(summary.withProxy) : '…'} />
          <Stat label={t('ltoStatOnTape')} value={summary ? formatBytes(summary.onTapeBytes) : '…'} />
          <Stat label={t('ltoStatRestoring')} value={summary ? nf.format(summary.restoresOpen) : '…'} hot={!!summary?.restoresOpen} onClick={() => setView('restores')} />
          <div className={`lto-agent ${agent ? (agent.online ? 'on' : 'off') : 'none'}`} title={agent ? `${agent.backend} · v${agent.version}` : t('ltoAgentNoneHint')}>
            <span className="lto-agent-dot" />
            <span>
              {t('ltoAgent')}
              <small>{agent ? (agent.online ? `${t('ltoAgentOnline')}${agent.loaded ? ` · ${t('ltoInDrive')} ${agent.loaded}` : ''}` : t('ltoAgentOffline')) : t('ltoAgentNone')}</small>
            </span>
          </div>
        </div>
        <div className="lto-head-actions">
          <a className="lto-btn ghost" href={`/api/videos/export.pdf?${params()}`} target="_blank" rel="noreferrer" title={t('pdf')}>
            🖨 PDF
          </a>
          {can('video.create') && (
            <button type="button" className="lto-btn primary" onClick={() => setDialog({ mode: 'new' })}>
              ＋ {t('videoNew')}
            </button>
          )}
        </div>
      </header>

      <div className="lto-body">
        <nav className="lto-nav" aria-label={t('tabVideo')}>
          <button type="button" className={view === 'search' ? 'on' : ''} onClick={() => setView('search')}>
            <span className="lto-nav-ico">⌕</span> {t('ltoViewSearch')}
          </button>
          <button type="button" className={view === 'tapes' ? 'on' : ''} onClick={() => setView('tapes')}>
            <span className="lto-nav-ico">▤</span> {t('ltoViewTapes')}
            {summary && <span className="lto-count">{nf.format(summary.tapes)}</span>}
          </button>
          <button type="button" className={view === 'restores' ? 'on' : ''} onClick={() => setView('restores')}>
            <span className="lto-nav-ico">⟲</span> {t('ltoViewRestores')}
            {!!summary?.restoresOpen && <span className="lto-count hot">{summary.restoresOpen}</span>}
          </button>
          <div className="lto-nav-foot">
            <TapeLegend />
          </div>
        </nav>

        {view === 'search' && (
          <>
            <section className="lto-main">
              <form className="lto-search" onSubmit={submit}>
                <div className="lto-searchbox">
                  <span aria-hidden="true">⌕</span>
                  <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ltoSearchPlaceholder')} aria-label={t('search')} />
                  <button type="submit" className="lto-btn primary" disabled={!!searching}>
                    {t('search')}
                  </button>
                </div>
                <div className="lto-chips">
                  <button type="button" className={`lto-chip${filtersOpen ? ' on' : ''}`} onClick={() => setFiltersOpen((o) => !o)} aria-expanded={filtersOpen}>
                    ⚙ {t('ltoFilters')}
                  </button>
                  <label className={`lto-chip${onlyProxy ? ' on' : ''}`}>
                    <input type="checkbox" checked={onlyProxy} onChange={(e) => setOnlyProxy(e.target.checked)} /> ▶ {t('ltoOnlyPreview')}
                  </label>
                  <label className={`lto-chip${withSummary ? ' on' : ''}`}>
                    <input type="checkbox" checked={withSummary} onChange={(e) => setWithSummary(e.target.checked)} /> ¶ {t('ltoWithSummary')}
                  </label>
                  {tapeId && (
                    <button type="button" className="lto-chip on" onClick={() => setTapeId(null)} title={t('ltoClearTape')}>
                      <b className="lto-barcode-chip">{tapeId.barcode}</b> ✕
                    </button>
                  )}
                  {videoType && <span className="lto-chip on">{options.types.find((x) => x.value === videoType)?.label}</span>}
                  {(eventDate || from || to) && <span className="lto-chip on">📅 {eventDate ? fmtDateInput(eventDate) : `${from ? fmtDateInput(from) : '…'} – ${to ? fmtDateInput(to) : '…'}`}</span>}
                  {categoryIds.length > 0 && <span className="lto-chip on">{categoryIds.length} {t('ltoCategoriesN')}</span>}
                  <button type="button" className="lto-chip ghost" onClick={clear}>
                    {t('clear')}
                  </button>
                </div>
                {filtersOpen && (
                  <div className="lto-filters">
                    <label>
                      {t('videoType')}
                      <select value={videoType} onChange={(e) => setVideoType(e.target.value)}>
                        <option value="">{t('anyType')}</option>
                        {options.types.map((x) => (
                          <option key={x.value} value={x.value}>
                            {x.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {t('eventDate')}
                      <DateInput value={eventDate} onChange={setEventDate} />
                    </label>
                    <label>
                      {t('fromDate')}
                      <DateInput value={from} onChange={setFrom} />
                    </label>
                    <label>
                      {t('toDate')}
                      <DateInput value={to} onChange={setTo} />
                    </label>
                    <label>
                      {t('tapeNo')}
                      <input type="text" maxLength={10} value={tapeNo} onChange={(e) => setTapeNo(e.target.value)} />
                    </label>
                    <label>
                      {t('itemNo')}
                      <input type="number" min={0} value={itemNo} onChange={(e) => setItemNo(e.target.value)} />
                    </label>
                    <label>
                      {t('records')}
                      <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                        {[20, 40, 80, 100, 500].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="lto-cats">
                      <span>{t('videoCategories')}</span>
                      {options.categories.map((c) => (
                        <label key={c.id} className={`lto-chip${categoryIds.includes(c.id) ? ' on' : ''}`}>
                          <input type="checkbox" checked={categoryIds.includes(c.id)} onChange={() => toggleCategory(c.id)} /> {c.active ? c.name : `* ${c.name}`}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </form>
              {error && <div className="lto-err">{error}</div>}
              {notice && <div className="lto-ok">{notice}</div>}
              <div className="lto-resultbar">
                <span>{data ? `${nf.format(data.videos.length)}${data.more ? '+' : ''} ${t('ltoResults')}` : ''}</span>
                {data?.more && <span className="muted">{t('ltoMore')}</span>}
              </div>
              <div className={`lto-results${searching ? ' busy' : ''}`} ref={list}>
                {searching && (
                  <div className="lto-searching">
                    <TapeRobot barcode={searching} caption={t('ltoRobotSearch')} />
                  </div>
                )}
                {data?.videos.length === 0 && !searching && <div className="lto-empty">{t('videosNone')}</div>}
                {data?.videos.map((v) => (
                  <button key={v.id} type="button" data-id={v.id} className={`lto-row${selected?.id === v.id ? ' sel' : ''}`} onClick={() => setSelected(v)} onDoubleClick={() => setDialog({ mode: 'edit', video: v })}>
                    <span className="lto-thumb">
                      {v.hasProxy ? <img alt="" loading="lazy" src={`/api/archive/items/${v.id}/thumb?v=${v.proxyAt ?? 0}`} onError={(e) => (e.currentTarget.style.visibility = 'hidden')} /> : <Cartridge barcode={v.tapeNo ?? '—'} size="sm" />}
                      {v.hasProxy && <span className="lto-thumb-play">▶</span>}
                      <span className="lto-thumb-dur">{hhmmss(v.durationS)}</span>
                    </span>
                    <span className="lto-row-main">
                      <span className="lto-row-title">{v.title}</span>
                      {withSummary && v.summary && <span className="lto-row-summary">{v.summary.slice(0, 260)}</span>}
                      <span className="lto-row-meta">
                        <span>{fmtDateInput(v.eventDate)}</span>
                        {v.videoTypeLabel && <span className={`lto-type ${v.videoType ?? ''}`}>{v.videoTypeLabel}</span>}
                        {v.categories.slice(0, 3).map((c) => (
                          <span key={c.id} className="lto-cat">
                            {c.name}
                          </span>
                        ))}
                      </span>
                    </span>
                    <span className="lto-row-side">
                      {v.tapeNo && <b className="lto-barcode-chip">{v.tapeNo}</b>}
                      {v.itemNo !== null && <span className="muted small">#{v.itemNo}</span>}
                      {v.restoreOpen && <span className={`lto-pill st-${v.restoreOpen}`}>⟲</span>}
                      {v.fileSize ? <span className="muted small">{formatBytes(v.fileSize)}</span> : null}
                    </span>
                  </button>
                ))}
              </div>
            </section>
            {selected ? (
              <ItemPanel key={`${selected.id}:${previewTick}`} video={selected} ingest={summary?.ingest ?? null} onEdit={() => setDialog({ mode: 'edit', video: selected })} onDelete={() => void remove(selected)} onOpenTape={openTape} onRestoresChanged={loadSummary} />
            ) : (
              <aside className="lto-panel lto-panel-empty">{t('ltoPickItem')}</aside>
            )}
          </>
        )}
        {view === 'tapes' && <TapesView key={openTapeId ?? 'tapes'} openTapeId={openTapeId} onOpenItem={(videoId, tape) => void openItem(videoId, tape)} />}
        {view === 'restores' && <RestoresView onOpenItem={(id) => void openItem(id)} onChanged={loadSummary} />}
      </div>
      <PreviewTray onOpen={(id) => void openItem(id)} />
    </div>
    {/* outside the archive's dark page: the card keeps the HPress look */}
    {dialog && (
      <VideoDialog
        mode={dialog.mode}
        video={dialog.mode === 'edit' ? dialog.video : null}
        options={options}
        onClose={closeDialog}
        onSaved={onSaved}
        extra={({ readOnly, busy, setDuration }) =>
          !readOnly && (
            <PreviewPicker
              picked={picked}
              ingest={summary?.ingest ?? null}
              hasPreview={dialog.mode === 'edit' && !!dialog.video.hasProxy}
              disabled={busy}
              onPick={(p) => {
                setPicked(p);
                if (p?.look?.durationMs) setDuration(p.look.durationMs);
              }}
            />
          )
        }
      />
    )}
    </>
  );
}

function Stat({ label, value, hot, onClick }: { label: string; value: string; hot?: boolean; onClick?: () => void }) {
  const body = (
    <>
      <b>{value}</b>
      <small>{label}</small>
    </>
  );
  return onClick ? (
    <button type="button" className={`lto-stat${hot ? ' hot' : ''}`} onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className="lto-stat">{body}</div>
  );
}

/** The generations' colours, as a key under the views. */
function TapeLegend() {
  return (
    <div className="lto-legend">
      {['LTO-6', 'LTO-7', 'LTO-8', 'LTO-9'].map((g) => (
        <span key={g}>
          <Cartridge barcode={g.replace('LTO-', 'L')} generation={g} size="sm" />
        </span>
      ))}
    </div>
  );
}
